import type { SessionInfo, Tenant } from '@sirius/contracts/admin'
import type { TenantHost } from './tenantHost'

export type TenantRef = Pick<Tenant, 'id' | 'slug' | 'name' | 'status'>

export type TenantDecision =
  | { type: 'render'; tenant: TenantRef }
  | { type: 'redirect'; slug: string }
  | { type: 'notFound' }
  | { type: 'noTenant' }

/**
 * What admin-web shows on a host. `hostTenant` is the tenant of the host's
 * slug if the user can see it (backend scope), `fallbackTenant` the one to
 * send them to (active tenant, else first membership, else for a platform
 * role (admin, technician) the first tenant). Tenant users never learn whether another tenant's slug
 * exists: they are sent to their own tenant either way.
 */
export function decideTenant({
  host,
  hasPlatformRole,
  hostTenant,
  fallbackTenant,
}: {
  host: TenantHost
  /** a platform admin or a technician: sees every tenant */
  hasPlatformRole: boolean
  hostTenant: TenantRef | null
  fallbackTenant: TenantRef | null
}): TenantDecision {
  if (host.kind === 'tenant' && hostTenant !== null) {
    return { type: 'render', tenant: hostTenant }
  }
  if (host.kind === 'tenant' && hasPlatformRole) {
    return { type: 'notFound' }
  }
  if (fallbackTenant === null) {
    return { type: 'noTenant' }
  }
  if (host.kind === 'tenant' && host.slug === fallbackTenant.slug) {
    return { type: 'noTenant' }
  }
  return { type: 'redirect', slug: fallbackTenant.slug }
}

type MembershipInfo = Pick<SessionInfo, 'tenantIds' | 'memberships'>

/** The user's role in tenant `tenantId`; undefined: no member of it. */
export function hostRole(
  info: MembershipInfo,
  tenantId: string,
): string | undefined {
  return info.memberships.find((membership) => membership.tenantId === tenantId)
    ?.role
}

/**
 * What a fresh `auth.me` of a tenant user says about the host tenant the
 * app renders, against their role in it as last seen (`roleBefore`;
 * undefined: unknown): `lost` once they are no member of it (removed: the
 * page must reload, so the gate decides again), `roleChanged` when their
 * role in it differs (access answers are stale), else `unchanged`.
 */
export function hostMembershipChange({
  roleBefore,
  after,
  tenantId,
}: {
  roleBefore: string | undefined
  after: MembershipInfo
  tenantId: string
}): 'lost' | 'roleChanged' | 'unchanged' {
  if (!after.tenantIds.includes(tenantId)) {
    return 'lost'
  }
  return roleBefore !== undefined && roleBefore !== hostRole(after, tenantId)
    ? 'roleChanged'
    : 'unchanged'
}
